import { useCallback, useContext, useEffect, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import axios from 'axios';

import { SyntiroAgentApiError, askSyntiro, toProblemDetail } from '../services/ask.service';
import { generateUuidV4 } from '../../../util/uuid';
import { ASK_MESSAGE_MAX_LENGTH } from '../types/syntiro-agent';
import type { AskAttachment, AskReport, AskTurn } from '../types/syntiro-agent';
import {
  captureScreenRaster,
  encodeCanvasImageToBase64
} from '../../../lib/screen-capture/screen-capture';
import { REPORT_VISUAL_DATA_TOOL_LABEL } from '../tools/report/visual-data';
import { getViewByPathname } from '../../../lib/app/navigation';
import { GroupContext } from '../../../context/GroupContext';
import { CompanyContext } from '../../../context/CompanyContext';
import { AskSyntiroContext } from '../../../context/AskSyntiroContext';

const EMPTY_REPLY_ERROR_MESSAGE = "Syntiro didn't return an answer. Please try again.";
const FALLBACK_ERROR_MESSAGE = 'Something went wrong. Please try again.';

/** Hard cap on the encoded screen capture size.*/
const SCREEN_CAPTURE_BYTES_HARD_CAP = 1_048_576;

/** Ceiling on agent turns spent answering one question — each is a `POST /ask`, which the service
 * handles as a turn. Two allows the initial ask plus one re-send carrying a tool call's data. */
const MAX_AGENT_TURNS = 2;

/** Yields two frames, so a just-set pending-state update (e.g. `isAwaitingReply`) paints before
 * the synchronous `captureScreenRaster` call blocks the main thread. */
function waitForNextPaint(): Promise<void> {
  return new Promise((resolve) => {
    requestAnimationFrame(() => requestAnimationFrame(() => resolve()));
  });
}

/**
 * Captures and encodes the current screen as a system attachment, or `undefined` if capture or
 * encoding did not produce a usable image. `encodeCanvasImageToBase64` enforces the byte cap, so an
 * over-budget capture is simply dropped and the turn proceeds without it.
 */
async function captureAskSyntiroScreen(): Promise<AskAttachment | undefined> {
  await waitForNextPaint();
  const screenCaptureResult = await captureScreenRaster();
  if (screenCaptureResult.status !== 'ok') {
    return undefined;
  }
  const encodedCanvasImage = await encodeCanvasImageToBase64(
    screenCaptureResult.canvas,
    SCREEN_CAPTURE_BYTES_HARD_CAP
  );
  if (!encodedCanvasImage) {
    return undefined;
  }
  return {
    origin: 'system',
    format: 'image/webp',
    encoded: encodedCanvasImage.dataBase64,
    widthPx: encodedCanvasImage.widthPx,
    heightPx: encodedCanvasImage.heightPx,
    capturedAtMs: Date.now()
  };
}

export interface UseAskSyntiroResult {
  turns: AskTurn[];
  isAwaitingReply: boolean;
  errorMessage: string | null;
  sendMessage: (message: string) => Promise<void>;
  retryLastTurn: () => Promise<void>;
  stopTurn: () => void;
}

/**
 * Owns the Ask Syntiro transcript, in-flight state, and `sessionId` reuse for
 * the current group/company.
 * The conversation survives navigation and resets only on a tenant change.
 */
export function useAskSyntiro(): UseAskSyntiroResult {
  const { group } = useContext(GroupContext);
  const { company } = useContext(CompanyContext);
  const { resolveAskContext, captureAskReport, fulfilReportDataRequest } =
    useContext(AskSyntiroContext);
  const { pathname: route } = useLocation();
  const groupId = group?.id;
  const companyId = company?.id;

  const [turns, setTurns] = useState<AskTurn[]>([]);
  const [isAwaitingReply, setIsAwaitingReply] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  const turnsRef = useRef<AskTurn[]>(turns);
  const sessionIdRef = useRef<string | null>(null);
  const isSendingRef = useRef(false);
  const requestGenerationRef = useRef(0);
  const abortControllerRef = useRef<AbortController | null>(null);
  const previousScopeKeyRef = useRef<string | null>(null);
  const scopeKey = groupId && companyId ? `${groupId}:${companyId}:${route}` : null;

  useEffect(() => {
    turnsRef.current = turns;
  }, [turns]);

  useEffect(() => {
    if (scopeKey === null) {
      return;
    }

    const previousScopeKey = previousScopeKeyRef.current;

    if (previousScopeKey !== null && previousScopeKey !== scopeKey) {
      abortControllerRef.current?.abort();
      requestGenerationRef.current += 1;
      sessionIdRef.current = null;
      setTurns([]);
      setErrorMessage(null);
      setIsAwaitingReply(false);
    }

    previousScopeKeyRef.current = scopeKey;
  }, [scopeKey]);

  useEffect(() => {
    return () => {
      abortControllerRef.current?.abort();
      requestGenerationRef.current += 1;
    };
  }, []);

  const runTurn = useCallback(
    async (
      message: string,
      options: { appendUserTurn: boolean; captureTurnScreen: boolean }
    ): Promise<void> => {
      if (!groupId || !companyId) return;
      if (isSendingRef.current) return;

      isSendingRef.current = true;
      setIsAwaitingReply(true);
      setErrorMessage(null);

      const generation = requestGenerationRef.current;

      if (options.appendUserTurn) {
        const userTurn: AskTurn = { id: generateUuidV4(), author: 'user', text: message };
        setTurns((current) => [...current, userTurn]);
      }

      // Rastering a report view would only yield blank app chrome around the embed.
      const isPowerBiView = getViewByPathname(route)?.require_pbi_report === true;
      const shouldCaptureScreen = options.captureTurnScreen && !isPowerBiView;

      // All three groundings are captured for this question, in parallel: the report's manifest and
      // filter state describe the page as it stands now rather than as it stood when it loaded.
      const [screenCapture, resolvedContext, capturedAskReport] = await Promise.all([
        shouldCaptureScreen ? captureAskSyntiroScreen() : Promise.resolve(undefined),
        resolveAskContext(),
        isPowerBiView ? captureAskReport() : Promise.resolve(undefined)
      ]);

      const attachments = screenCapture ? [screenCapture] : [];

      const controller = new AbortController();
      abortControllerRef.current = controller;

      let agentTurnsRemaining = capturedAskReport ? MAX_AGENT_TURNS : 1;
      let turnAskReport: AskReport | undefined = capturedAskReport;

      try {
        while (agentTurnsRemaining > 0) {
          agentTurnsRemaining -= 1;

          const response = await askSyntiro(
            {
              app: { groupId, companyId, route },
              sessionId: sessionIdRef.current ?? undefined,
              message,
              context: resolvedContext,
              attachments,
              ...(turnAskReport ? { report: turnAskReport } : {})
            },
            controller.signal
          );

          if (requestGenerationRef.current !== generation) return;

          sessionIdRef.current = response.sessionId;
          const toolCallToFulfil = agentTurnsRemaining > 0 ? response.toolCall : undefined;

          if (toolCallToFulfil) {
            turnAskReport = await fulfilReportDataRequest(toolCallToFulfil);
            const toolTurn: AskTurn = {
              id: generateUuidV4(),
              author: 'tool',
              text: REPORT_VISUAL_DATA_TOOL_LABEL
            };

            setTurns((current) => [...current, toolTurn]);
            continue;
          }

          if (response.reply.trim() === '') {
            setErrorMessage(EMPTY_REPLY_ERROR_MESSAGE);
            return;
          }

          const agentTurn: AskTurn = {
            id: generateUuidV4(),
            author: 'agent',
            text: response.reply
          };

          setTurns((current) => [...current, agentTurn]);
          return;
        }
      } catch (err: unknown) {
        if (axios.isCancel(err)) return;
        if (requestGenerationRef.current !== generation) return;
        const problem = err instanceof SyntiroAgentApiError ? err.problem : toProblemDetail(err);
        setErrorMessage(problem.detail ?? problem.title ?? FALLBACK_ERROR_MESSAGE);
      } finally {
        if (requestGenerationRef.current === generation) {
          setIsAwaitingReply(false);
        }
        isSendingRef.current = false;
        if (abortControllerRef.current === controller) {
          abortControllerRef.current = null;
        }
      }
    },
    [groupId, companyId, route, resolveAskContext, captureAskReport, fulfilReportDataRequest]
  );

  const sendMessage = useCallback(
    async (message: string): Promise<void> => {
      const trimmedMessage = message.trim();
      if (!trimmedMessage || trimmedMessage.length > ASK_MESSAGE_MAX_LENGTH) return;
      await runTurn(trimmedMessage, { appendUserTurn: true, captureTurnScreen: true });
    },
    [runTurn]
  );

  const retryLastTurn = useCallback(async (): Promise<void> => {
    const lastUserTurn = [...turnsRef.current].reverse().find((turn) => turn.author === 'user');
    if (!lastUserTurn) return;
    await runTurn(lastUserTurn.text, { appendUserTurn: false, captureTurnScreen: true });
  }, [runTurn]);

  const stopTurn = useCallback((): void => {
    abortControllerRef.current?.abort();
    requestGenerationRef.current += 1;
    isSendingRef.current = false;
    setIsAwaitingReply(false);
    setErrorMessage(null);
  }, []);

  return { turns, isAwaitingReply, errorMessage, sendMessage, retryLastTurn, stopTurn };
}
